import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  ArrowLeft, DownloadSimple, Plus, Trash, PaperPlaneTilt, CheckCircle,
  Receipt, FloppyDisk, MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

import DateInput from "../components/DateInput";
const STATUS = {
  draft:        { label: "Draft",        cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  issued:       { label: "Issued",       cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  acknowledged: { label: "Acknowledged", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  paid:         { label: "Paid",         cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
};

const STANDARD_UNIT_TYPES = ["Hours", "Miles", "Sqm"];

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2));

// One blank job line — admin can edit every field
const newLine = (form) => ({
  id: uid(),
  date: "",
  task: "",
  address: "",
  units: 0,
  unit_type: "Hours",
  cost: 0,
  rate_1: form?.rate_1_default || 0,
  rate_2: 0,
  job_id: "",
  job_reference: "",
});

// (units × cost) + rate_1 [+ rate_2 when the second rate column is on]
const calcLineTotal = (l, rate2On = true) =>
  (Number(l.units) || 0) * (Number(l.cost) || 0)
  + (Number(l.rate_1) || 0)
  + (rate2On ? (Number(l.rate_2) || 0) : 0);

export default function InHousePoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [po, setPo] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]);
  const [receipts, setReceipts] = useState([]); // receipts linked from expenses lines
  const [reviewing, setReviewing] = useState("");

  const isAdmin = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const isInHouse = user?.role === "in_house";
  const isDraft = po?.status === "draft";
  const canEdit = isAdmin && isDraft;
  const canAcknowledge = isInHouse && po?.status === "issued";

  const fetchPo = useCallback(async () => {
    setLoading(true);
    try {
      // Operatives must use the portal endpoint (admin endpoint is admin-only-via-tenant-query)
      const endpoint = isInHouse
        ? `/in-house-portal/pos/${id}`
        : `/in-house/pos/${id}`;
      const { data } = await API.get(endpoint);
      setPo(data);
      setForm({
        ...data,
        lines: data.lines && data.lines.length ? data.lines : [],
      });
    } catch { toast.error("Couldn't load PO"); navigate(isInHouse ? "/my-work" : "/in-house/pos"); }
    finally { setLoading(false); }
  }, [id, navigate, isInHouse]);

  useEffect(() => { fetchPo(); }, [fetchPo]);

  // Receipts behind the expenses lines (admin only)
  const hasExpenseLines = (po?.lines || []).some(l => l.kind === "expenses");
  useEffect(() => {
    if (!isAdmin || !po?.id || !hasExpenseLines) { setReceipts([]); return; }
    API.get(`/in-house/pos/${po.id}/expenses`).then(({ data }) => setReceipts(data.expenses || [])).catch(() => setReceipts([]));
  }, [isAdmin, po?.id, po?.updated_at, hasExpenseLines]);

  const reviewReceipt = async (expenseId, status) => {
    setReviewing(expenseId);
    try {
      await handleSaveSilent();
      const { data } = await API.post(`/in-house/pos/${id}/expenses/${expenseId}/review`, { status });
      setPo(data.po);
      setForm({ ...data.po, lines: data.po.lines || [] });
      setReceipts(data.expenses || []);
      toast.success(status === "approved" ? "Receipt approved — expenses line updated" : "Receipt rejected — removed from the expenses total");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update receipt");
    } finally { setReviewing(""); }
  };

  // Fetch recent jobs assigned to the operative (for the job picker)
  useEffect(() => {
    if (!po?.operative_id) return;
    API.get(`/jobs?assigned_to=${po.operative_id}&limit=30`).then(({ data }) => {
      setRecentJobs(Array.isArray(data) ? data : (data.jobs || []));
    }).catch(() => {});
  }, [po?.operative_id]);

  // ─── Computed totals ─────────────────────────────────────────────
  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, vat: 0, total: 0 };
    const subtotal = (form.lines || []).reduce((s, l) => s + calcLineTotal(l, !!form.rate_2_enabled), 0);
    const vat = form.vat_enabled ? subtotal * ((Number(form.vat_rate) || 0) / 100) : 0;
    return {
      subtotal: Number(subtotal.toFixed(2)),
      vat: Number(vat.toFixed(2)),
      total: Number((subtotal + vat).toFixed(2)),
    };
  }, [form]);

  // ─── Line mutations ──────────────────────────────────────────────
  const updateLine = (idx, patch) =>
    setForm(f => ({ ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = () =>
    setForm(f => ({ ...f, lines: [...f.lines, newLine(f)] }));
  const removeLine = (idx) =>
    setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const pickJobForLine = (idx, job) => {
    const addr = [job.street_address, job.town_city, job.postcode].filter(Boolean).join(", ");
    const date = job?.schedule?.start_date || "";
    updateLine(idx, {
      task: job.title || "",
      address: addr,
      date: date || undefined,
      job_id: job.id,
      job_reference: job.reference || "",
    });
  };

  // ─── Save / send / ack / paid ────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title, period_start: form.period_start, period_end: form.period_end,
        rate_1_label: form.rate_1_label, rate_2_label: form.rate_2_label, rate_2_enabled: !!form.rate_2_enabled,
        rate_1_default: Number(form.rate_1_default) || 0,
        rate_2_default: Number(form.rate_2_default) || 0,
        vat_enabled: !!form.vat_enabled,
        vat_rate: Number(form.vat_rate) || 0,
        notes: form.notes || "",
        lines: form.lines,
      };
      await API.put(`/in-house/pos/${id}`, payload);
      toast.success("Saved");
      await fetchPo();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await handleSaveSilent();
      const { data } = await API.post(`/in-house/pos/${id}/send`);
      toast.success(data.message);
      setShowSendDialog(false);
      await fetchPo();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally { setSending(false); }
  };

  const handleSaveSilent = async () => {
    if (!canEdit) return;
    const payload = {
      title: form.title, period_start: form.period_start, period_end: form.period_end,
      rate_1_label: form.rate_1_label, rate_2_label: form.rate_2_label, rate_2_enabled: !!form.rate_2_enabled,
      rate_1_default: Number(form.rate_1_default) || 0,
      rate_2_default: Number(form.rate_2_default) || 0,
      vat_enabled: !!form.vat_enabled,
      vat_rate: Number(form.vat_rate) || 0,
      notes: form.notes || "",
      lines: form.lines,
    };
    await API.put(`/in-house/pos/${id}`, payload);
  };

  const handleMarkPaid = async () => {
    try {
      await API.post(`/in-house/pos/${id}/mark-paid`);
      toast.success("Marked paid");
      fetchPo();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't mark paid"); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/in-house/pos/${id}`);
      toast.success(`Deleted ${po.po_number}`);
      navigate("/in-house/pos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete PO");
      setDeleting(false);
    }
  };

  const [acking, setAcking] = useState(false);
  const handleAcknowledge = async () => {
    setAcking(true);
    try {
      await API.post(`/in-house-portal/pos/${id}/acknowledge`);
      toast.success("Acknowledged — your admin has been notified");
      fetchPo();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't acknowledge"); }
    finally { setAcking(false); }
  };

  // ── Auto-fill draft from this operative's schedule ─────────────────
  const [autoFilling, setAutoFilling] = useState(false);
  const handleAutoFillSchedule = async () => {
    if (!canEdit) return;
    if (!form.period_start || !form.period_end) {
      toast.error("Set period start + end first");
      return;
    }
    setAutoFilling(true);
    try {
      const { data } = await API.post("/in-house/pos/preview-from-schedule", {
        operative_id: po.operative_id,
        period_start: form.period_start,
        period_end: form.period_end,
        rate_1_default: Number(form.rate_1_default) || 0,
        rate_2_default: form.rate_2_enabled ? (Number(form.rate_2_default) || 0) : 0,
      });
      if (data.total_jobs === 0) {
        toast.info("No jobs in this period for this operative.");
        return;
      }
      // Append schedule lines, skipping anything already present (same job / expenses / mileage day)
      const lineKey = (l) => l.source_key || (l.job_id ? `job:${l.job_id}` : null);
      const existingKeys = new Set((form.lines || []).map(lineKey).filter(Boolean));
      const newLines = data.lines.filter(l => !existingKeys.has(lineKey(l)));
      const merged = [...(form.lines || []), ...newLines]
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      setForm(f => ({ ...f, lines: merged }));
      const extras = [data.expense_lines ? `${data.expense_lines} expenses` : "", data.mileage_lines ? `${data.mileage_lines} mileage` : ""].filter(Boolean).join(", ");
      toast.success(`Added ${newLines.length} line${newLines.length === 1 ? "" : "s"} from the schedule${extras ? ` (incl. ${extras})` : ""}. Day rate is on the first job of each day only.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't auto-fill from schedule");
    } finally { setAutoFilling(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await API.get(`/in-house/pos/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${po.po_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download PDF"); }
  };

  if (loading || !po || !form) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  const status = STATUS[po.status] || STATUS.draft;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5" data-testid="in-house-po-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(isInHouse ? "/my-work" : "/in-house/pos")} data-testid="po-back-btn">
        <ArrowLeft size={14} className="mr-1" /> {isInHouse ? "Back to my work" : "Back to POs"}
      </Button>

      {/* Acknowledgement banner */}
      {isInHouse && po.status === "issued" && (
        <div className="border-2 border-dashed border-blue-300 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg p-4" data-testid="po-ack-banner">
          <div className="flex items-start gap-3 flex-wrap">
            <Receipt size={20} weight="duotone" className="text-blue-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm">Please acknowledge receipt</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Confirm you&apos;ve received this PO. You can then use it as the basis for your invoice (download the PDF below).
              </p>
            </div>
            <Button onClick={handleAcknowledge} disabled={acking} className="bg-blue-600 hover:bg-blue-700" data-testid="po-acknowledge-btn">
              <CheckCircle size={14} className="mr-1" /> {acking ? "Acknowledging…" : "Acknowledge"}
            </Button>
          </div>
        </div>
      )}
      {po.status === "acknowledged" && (
        <div className="border-2 border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 rounded-lg p-4" data-testid="po-acked-banner">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-semibold text-sm">Acknowledged{po.acknowledged_by_name ? ` by ${po.acknowledged_by_name}` : ""}</h3>
              {po.acknowledged_at && (
                <p className="text-xs text-muted-foreground mt-0.5">{new Date(po.acknowledged_at).toLocaleString()} — awaiting payment.</p>
              )}
            </div>
          </div>
        </div>
      )}
      {po.status === "paid" && (
        <div className="border-2 border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-lg p-4" data-testid="po-paid-banner">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-semibold text-sm">Paid</h3>
              {po.paid_at && <p className="text-xs text-muted-foreground mt-0.5">{new Date(po.paid_at).toLocaleString()}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold font-[Chivo]">Purchase Order</h1>
            <span className="font-mono text-sm text-muted-foreground">{po.po_number}</span>
            <Badge className={status.cls} data-testid="po-status-badge">{status.label}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {po.operative_name} · {po.title}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="po-download-pdf">
            <DownloadSimple size={14} className="mr-1" /> Download PDF
          </Button>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="po-save-btn">
              <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          )}
          {canEdit && (
            <Button size="sm" onClick={() => setShowSendDialog(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="po-send-btn">
              <PaperPlaneTilt size={14} className="mr-1" /> Send to Operative
            </Button>
          )}
          {isAdmin && (po.status === "issued" || po.status === "acknowledged") && (
            <Button size="sm" onClick={handleMarkPaid} className="bg-emerald-600 hover:bg-emerald-700" data-testid="po-mark-paid-btn">
              <CheckCircle size={14} className="mr-1" /> Mark Paid
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
              data-testid="po-delete-btn"
            >
              <Trash size={14} className="mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Header form (admin draft only) — collapse into inline edits */}
      {canEdit && (
        <div className="border border-border rounded-lg p-4 bg-card grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="po-header-form">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="po-title" />
          </div>
          <div>
            <Label className="text-xs">Period start</Label>
            <DateInput value={form.period_start || ""} onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Period end</Label>
            <DateInput value={form.period_end || ""} onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Day rate name</Label>
            <Input value={form.rate_1_label || ""} onChange={e => setForm(f => ({ ...f, rate_1_label: e.target.value }))} placeholder="Day rate" data-testid="po-rate1-label" />
          </div>
          <div className="sm:col-span-2 flex items-center gap-3 rounded-md border border-border px-3 py-2" data-testid="po-second-rate-block">
            <Switch checked={!!form.rate_2_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, rate_2_enabled: v }))} data-testid="po-second-rate-toggle" />
            <div className="flex-1 min-w-0">
              <Label className="text-xs">Second rate column</Label>
              <p className="text-[11px] text-muted-foreground truncate">{form.rate_2_enabled ? "Shown on the PO and included in line totals." : "Off — hidden from the PO and excluded from totals."}</p>
            </div>
            {form.rate_2_enabled && (
              <Input value={form.rate_2_label || ""} onChange={e => setForm(f => ({ ...f, rate_2_label: e.target.value }))} placeholder="Overtime rate" className="w-40" data-testid="po-rate2-label" />
            )}
          </div>
        </div>
      )}

      {/* Per-job line items table */}
      <div className="border border-border rounded-lg overflow-x-auto bg-card" data-testid="po-day-rows-table">
        {canEdit && (
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border bg-blue-50/30 dark:bg-blue-950/10">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MagicWand size={14} weight="duotone" className="text-blue-600" />
              <span>Pull every job assigned to <strong className="text-foreground">{po.operative_name}</strong> in the period: day rate on the first job of each day, an expenses line per job with receipts, and a mileage line per day.</span>
            </div>
            <Button
              variant="outline" size="sm"
              onClick={handleAutoFillSchedule}
              disabled={autoFilling || !form.period_start || !form.period_end}
              className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 shrink-0"
              data-testid="po-auto-fill-schedule-btn"
            >
              <MagicWand size={14} className="mr-1" /> {autoFilling ? "Pulling…" : "Auto-fill from schedule"}
            </Button>
          </div>
        )}
        <table className="w-full text-sm" data-testid="po-lines-table">
          <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-3 text-left font-semibold w-32">Date</th>
              <th className="px-3 py-3 text-left font-semibold min-w-[220px]">Task / Address</th>
              <th className="px-3 py-3 text-right font-semibold w-20">Units</th>
              <th className="px-3 py-3 text-left font-semibold w-28">Unit Type</th>
              <th className="px-3 py-3 text-right font-semibold w-24">Cost (£)</th>
              <th className="px-3 py-3 text-right font-semibold w-24">{form.rate_1_label || "Day rate"} (£)</th>
              {form.rate_2_enabled && <th className="px-3 py-3 text-right font-semibold w-24" data-testid="po-rate2-header">{form.rate_2_label || "Rate 2"} (£)</th>}
              <th className="px-3 py-3 text-right font-semibold w-28">Day Total (£)</th>
              {canEdit && <th className="w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {form.lines.length === 0 && (
              <tr>
                <td colSpan={form.rate_2_enabled ? 9 : 8} className="text-center text-muted-foreground py-10 text-sm">
                  No job lines yet. {canEdit && "Click 'Add line' below to start."}
                </td>
              </tr>
            )}
            {form.lines.map((line, idx) => {
              const lineTotal = calcLineTotal(line, !!form.rate_2_enabled);
              const matchingJob = line.job_id && recentJobs.find(j => j.id === line.job_id);
              return (
                <tr key={line.id} className="border-t border-border align-top" data-testid={`po-day-row-${idx}`}>
                  <td className="px-3 py-3">
                    <DateInput

                      value={line.date || ""}
                      disabled={!canEdit}
                      onChange={(e) => updateLine(idx, { date: e.target.value })}
                      className="text-xs"
                      data-testid={`po-day-date-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="space-y-1.5">
                      <Input
                        value={line.task}
                        disabled={!canEdit}
                        onChange={(e) => updateLine(idx, { task: e.target.value })}
                        placeholder="Task description"
                        className="text-xs font-semibold"
                        data-testid={`po-task-${idx}`}
                      />
                      <Input
                        value={line.address}
                        disabled={!canEdit}
                        onChange={(e) => updateLine(idx, { address: e.target.value })}
                        placeholder="Address"
                        className="text-xs text-muted-foreground"
                        data-testid={`po-address-${idx}`}
                      />
                      {canEdit && recentJobs.length > 0 && !matchingJob && (
                        <select
                          value=""
                          onChange={(e) => {
                            const job = recentJobs.find(j => j.id === e.target.value);
                            if (job) pickJobForLine(idx, job);
                          }}
                          className="text-[11px] border border-border rounded-md px-1.5 py-1 bg-card w-full"
                          data-testid={`po-job-picker-${idx}`}
                          title="Pre-fill from an assigned job"
                        >
                          <option value="">Link to an assigned job…</option>
                          {recentJobs.slice(0, 25).map(j => (
                            <option key={j.id} value={j.id}>{j.reference} · {(j.title || "").slice(0, 30)}</option>
                          ))}
                        </select>
                      )}
                      {(line.job_reference || (line.kind && line.kind !== "job")) && (
                        <p className="text-[10px] font-mono flex items-center gap-1.5" data-testid={`po-line-ref-${idx}`}>
                          {line.kind === "expenses" && <span className="uppercase text-[9px] font-semibold px-1 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300" data-testid={`po-line-kind-${idx}`}>Expenses</span>}
                          {line.kind === "mileage" && <span className="uppercase text-[9px] font-semibold px-1 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" data-testid={`po-line-kind-${idx}`}>Mileage</span>}
                          {line.job_reference && <span className="text-blue-600">↳ {line.job_reference}</span>}
                        </p>
                      )}
                      {line.kind === "expenses" && isAdmin && receipts.some(r => r.line_id === line.id) && (
                        <ul className="mt-1 space-y-1 border-l-2 border-amber-300 pl-2" data-testid={`po-line-receipts-${idx}`}>
                          {receipts.filter(r => r.line_id === line.id).map(r => (
                            <li key={r.id} className="flex items-center gap-2 flex-wrap text-[11px]" data-testid={`po-receipt-${r.id}`}>
                              <span className={`uppercase text-[9px] font-semibold px-1 rounded ${r.status === "approved" ? "bg-emerald-500/10 text-emerald-700" : r.status === "rejected" ? "bg-red-500/10 text-red-700 line-through" : "bg-amber-500/10 text-amber-700"}`} data-testid={`po-receipt-status-${r.id}`}>{r.status}</span>
                              <span className="font-medium">£{Number(r.amount || 0).toFixed(2)}</span>
                              <span className="text-muted-foreground truncate max-w-[220px]">{r.description || r.category}</span>
                              {r.receipt_url && <a href={r.receipt_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" data-testid={`po-receipt-view-${r.id}`}>View receipt</a>}
                              {canEdit && r.status !== "approved" && (
                                <button type="button" disabled={reviewing === r.id} onClick={() => reviewReceipt(r.id, "approved")} className="px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50" data-testid={`po-receipt-approve-${r.id}`}>Approve</button>
                              )}
                              {canEdit && r.status !== "rejected" && (
                                <button type="button" disabled={reviewing === r.id} onClick={() => reviewReceipt(r.id, "rejected")} className="px-1.5 py-0.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50" data-testid={`po-receipt-reject-${r.id}`}>Reject</button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Input
                      type="number"
                      step="any"
                      value={line.units || 0}
                      disabled={!canEdit}
                      onChange={(e) => updateLine(idx, { units: e.target.value })}
                      className="text-right text-xs"
                      data-testid={`po-units-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <UnitTypePicker
                      value={line.unit_type || ""}
                      disabled={!canEdit}
                      onChange={(v) => updateLine(idx, { unit_type: v })}
                      dtPrefix={`po-unit-type-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Input
                      type="number" step="0.01" value={line.cost || 0}
                      disabled={!canEdit}
                      onChange={(e) => updateLine(idx, { cost: e.target.value })}
                      className="text-right text-xs"
                      data-testid={`po-cost-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Input
                      type="number" step="0.01" value={line.rate_1 || 0}
                      disabled={!canEdit}
                      onChange={(e) => updateLine(idx, { rate_1: e.target.value })}
                      className="text-right text-xs"
                      data-testid={`po-day-rate1-${idx}`}
                    />
                  </td>
                  {form.rate_2_enabled && (
                    <td className="px-3 py-3 text-right">
                      <Input
                        type="number" step="0.01" value={line.rate_2 || 0}
                        disabled={!canEdit}
                        onChange={(e) => updateLine(idx, { rate_2: e.target.value })}
                        className="text-right text-xs"
                        data-testid={`po-day-rate2-${idx}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-3 text-right font-semibold" data-testid={`po-day-total-${idx}`}>
                    £{lineTotal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  {canEdit && (
                    <td className="px-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeLine(idx)} data-testid={`po-remove-day-${idx}`}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {canEdit && (
          <div className="p-3 border-t border-border">
            <Button variant="outline" size="sm" onClick={addLine} className="w-full" data-testid="po-add-day-btn">
              <Plus size={14} className="mr-1" /> Add line
            </Button>
          </div>
        )}
      </div>

      {/* Totals + VAT */}
      <div className="border border-border rounded-lg bg-card p-4 max-w-md ml-auto space-y-2" data-testid="po-totals-card">
        <Row label="Subtotal" value={totals.subtotal} />
        {canEdit && (
          <div className="flex items-center justify-between border-y border-border py-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.vat_enabled}
                onChange={(e) => setForm(f => ({ ...f, vat_enabled: e.target.checked }))}
                data-testid="po-vat-toggle"
              />
              Add VAT
            </label>
            {form.vat_enabled && (
              <div className="flex items-center gap-1">
                <Input
                  type="number" step="0.1" value={form.vat_rate}
                  onChange={(e) => setForm(f => ({ ...f, vat_rate: e.target.value }))}
                  className="w-20 text-right text-sm"
                  data-testid="po-vat-rate"
                /><span className="text-sm">%</span>
              </div>
            )}
          </div>
        )}
        {form.vat_enabled && (
          <Row label={`VAT (${Number(form.vat_rate || 0)}%)`} value={totals.vat} />
        )}
        <div className="border-t border-border pt-2">
          <Row label="Total" value={totals.total} bold testid="po-grand-total" />
        </div>
      </div>

      {/* Notes */}
      {(canEdit || form.notes) && (
        <div className="border border-border rounded-lg p-4 bg-card">
          <Label className="text-xs">Notes (visible on PDF)</Label>
          <Textarea
            value={form.notes || ""}
            onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Optional notes — bank details for invoicing, terms, etc."
            disabled={!canEdit}
            rows={3}
            className="mt-1"
            data-testid="po-notes"
          />
        </div>
      )}

      {/* Send dialog */}
      <Dialog open={showSendDialog} onOpenChange={(o) => { if (!o) setShowSendDialog(false); }}>
        <DialogContent className="max-w-md" data-testid="po-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Issue PO to {po.operative_name}</DialogTitle>
            <DialogDescription>
              This will save your changes, push a notification into their portal and lock the PO from edits.
              Total: <strong>£{totals.total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="po-send-confirm-btn">
              <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending…" : "Send to Operative"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(o) => { if (!o && !deleting) setShowDeleteDialog(false); }}>
        <DialogContent className="max-w-md" data-testid="po-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] flex items-center gap-2">
              <Trash size={18} className="text-destructive" /> Delete {po.po_number}?
            </DialogTitle>
            <DialogDescription>
              {po.status === "draft" ? (
                <>This draft will be permanently removed. You can&apos;t undo this.</>
              ) : (
                <>
                  This PO is <strong>{po.status}</strong> and has a total of <strong>£{totals.total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</strong>.
                  Deleting it removes the record from {po.operative_name}&apos;s portal too. The deletion is logged in the audit trail but the PO itself can&apos;t be restored.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting} data-testid="po-delete-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="po-delete-confirm-btn"
            >
              <Trash size={14} className="mr-1" /> {deleting ? "Deleting…" : "Delete PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold, testid }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className={bold ? "font-bold" : "text-muted-foreground"}>{label}</span>
      <span className={bold ? "font-bold text-lg" : "font-medium"}>
        £{Number(value || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
}

/**
 * Unit-type cell — dropdown with Hours / Miles / Sqm, plus an inline custom
 * option. When the user picks "Custom…" the cell flips to a text input.
 */
function UnitTypePicker({ value, disabled, onChange, dtPrefix }) {
  const isStandard = STANDARD_UNIT_TYPES.includes(value);
  const [customMode, setCustomMode] = useState(!!value && !isStandard);

  if (disabled) {
    return <span className="text-xs text-muted-foreground" data-testid={`${dtPrefix}-readonly`}>{value || "—"}</span>;
  }

  if (customMode) {
    return (
      <div className="flex items-center gap-1">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Custom"
          className="text-xs"
          data-testid={`${dtPrefix}-custom`}
        />
        <button
          type="button"
          className="text-[10px] text-muted-foreground hover:text-foreground"
          onClick={() => { setCustomMode(false); onChange("Hours"); }}
          title="Back to standard list"
          data-testid={`${dtPrefix}-reset`}
        >×</button>
      </div>
    );
  }
  return (
    <select
      value={value || ""}
      onChange={(e) => {
        if (e.target.value === "__custom__") {
          setCustomMode(true);
          onChange("");
        } else {
          onChange(e.target.value);
        }
      }}
      className="text-xs border border-border rounded-md px-2 py-1.5 bg-card w-full"
      data-testid={`${dtPrefix}-select`}
    >
      <option value="">—</option>
      {STANDARD_UNIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      <option value="__custom__">Custom…</option>
    </select>
  );
}
